import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchAccessReviewCampaign,
  fetchAccessReviewCampaigns,
  fetchAccountCredits,
  fetchAgents,
  fetchAutopilotFeed,
  fetchAutopilotInbox,
  fetchAutopilotOverview,
  fetchAlert,
  fetchAlertQuality,
  fetchAlerts,
  fetchBreakGlass,
  fetchCoverage,
  fetchCustomersUsage,
  fetchCustomerUsageRange,
  fetchFleet,
  fetchHomeDashboard,
  fetchIncident,
  fetchIncidents,
  fetchIncidentTimeline,
  fetchInfrastructureCell,
  fetchInfrastructureCells,
  fetchInfrastructureNode,
  fetchKBForService,
  fetchLeadershipSummary,
  fetchMeta,
  fetchMetricsCatalog,
  fetchMetricsQueryRange,
  fetchNotifications,
  fetchPeople,
  fetchPricebook,
  fetchPricingConfig,
  fetchRackBook,
  fetchRoles,
  fetchRotations,
  fetchSearch,
  fetchTeamsList,
  fetchService,
  fetchServiceCustomers,
  fetchServiceSignals,
  fetchServiceSignalsRange,
  fetchServiceVerbMetrics,
  fetchServiceLatency,
  fetchServiceSLOBurn,
  fetchServices,
  fetchSilences,
  fetchSop,
  fetchSopCoverage,
  fetchSopGraph,
  fetchSopGraphAnalytics,
  fetchSopGraphVersion,
  fetchSopGraphs,
  fetchSopRun,
  fetchSopRuns,
  fetchSops,
  fetchTenant,
  fetchTenants,
  fetchTerminal,
  fetchTerminalRecording,
  fetchTerminalReviews,
  fetchTerminals,
  fetchVulns,
  fetchWhoAmI,
  invokeAction,
  logout,
} from './client'
import type { AlertFilter, IncidentFilter, TerminalFilter } from './types'
import { type PresetWindow, type WindowSel, windowKey } from '@/routes/service/signalsWindow'

export const WHOAMI_QUERY_KEY = ['whoami']

/** Retries would just repeat the same 401 while unauthenticated — surface it once. */
export function useWhoAmI() {
  return useQuery({ queryKey: WHOAMI_QUERY_KEY, queryFn: fetchWhoAmI, retry: false })
}

/** Call after a successful login to make every scoped view refetch under the new persona. */
export function useRefreshAfterAuthChange() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries()
}

export function useLogout() {
  const refresh = useRefreshAfterAuthChange()
  return async () => {
    await logout()
    refresh()
  }
}

export function useHomeDashboard() {
  return useQuery({ queryKey: ['home-dashboard'], queryFn: fetchHomeDashboard })
}

export function useLeadershipSummary() {
  return useQuery({ queryKey: ['leadership-summary'], queryFn: fetchLeadershipSummary })
}

/** Rarely changes within a session — used for feature wiring flags like mcdAdminWired. */
export function useMeta() {
  return useQuery({ queryKey: ['meta'], queryFn: fetchMeta, staleTime: Infinity })
}

export function useServices() {
  return useQuery({ queryKey: ['services'], queryFn: fetchServices })
}

/** The fleet wall (Founder directive) — every service as a live tile, worst-first,
 * from THREE grouped by-svc Prometheus queries. Polls on a 30s cadence so the
 * wall stays live as the store records forward; the window (a preset, default
 * 15m) is part of the key so a range change refetches. Tiles are cheap (two tiny
 * inline-SVG sparklines each), so a whole-fleet refetch is one small request. */
export function useFleet(window: PresetWindow) {
  return useQuery({
    queryKey: ['metrics-fleet', window],
    queryFn: () => fetchFleet(window),
    refetchInterval: 30_000,
  })
}

export function useService(key: string) {
  return useQuery({ queryKey: ['service', key], queryFn: () => fetchService(key), enabled: !!key })
}

export function useCoverage() {
  return useQuery({ queryKey: ['coverage'], queryFn: fetchCoverage })
}

// Customer Insight / Support Workspace (M5/M6, ADR-0005 R2).

export function useTenants() {
  return useQuery({ queryKey: ['tenants'], queryFn: fetchTenants })
}

export function useTenant(id: string) {
  return useQuery({ queryKey: ['tenant', id], queryFn: () => fetchTenant(id), enabled: !!id })
}

export function useServiceCustomers(key: string) {
  return useQuery({ queryKey: ['service-customers', key], queryFn: () => fetchServiceCustomers(key), enabled: !!key })
}

/** Per-service golden signals (FR-OBS-01/02) — real RED metrics or an honest gap. */
export function useServiceSignals(key: string) {
  return useQuery({ queryKey: ['service-signals', key], queryFn: () => fetchServiceSignals(key), enabled: !!key })
}

/** The per-service time-series graphs (range-picker driven). refetchInterval
 * keeps the graphs live as Prometheus records forward; the selection (preset or
 * custom [start,end]) is part of the query key so a range change refetches.
 * `enabled` lets the compare overlay gate a second (shifted-window) fetch until
 * the base window's real bounds are known. */
export function useServiceSignalsRange(key: string, sel: WindowSel, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['service-signals-range', key, windowKey(sel)],
    queryFn: () => fetchServiceSignalsRange(key, sel),
    enabled: !!key && (opts?.enabled ?? true),
    refetchInterval: 30_000,
  })
}

/** The per-service per-operation (verb) breakdown table — real per-verb RED
 * numbers or an honest gap. Polls on the same 30s cadence as the range graphs
 * so the table stays live as Prometheus records forward. The endpoint is instant
 * (point-in-time, window-independent), so the key carries no window — every
 * consumer (KPI row, ops table) shares one live cache entry. */
export function useServiceVerbMetrics(key: string) {
  return useQuery({
    queryKey: ['service-verb-metrics', key],
    queryFn: () => fetchServiceVerbMetrics(key),
    enabled: !!key,
    refetchInterval: 30_000,
  })
}

/** The per-service latency percentiles (p50/p95/p99), range-picker driven and
 * polled live like the RED graphs. The selection is part of the key so a range
 * change refetches. */
export function useServiceLatency(key: string, sel: WindowSel, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['service-latency', key, windowKey(sel)],
    queryFn: () => fetchServiceLatency(key, sel),
    enabled: !!key && (opts?.enabled ?? true),
    refetchInterval: 30_000,
  })
}

/** The per-service SLO-burn tiles (declared SLOs evaluated against the store).
 * Instant/window-independent, so no window in the key; polled live so a
 * not-evaluable SLO flips the moment its backing series starts flowing. */
export function useServiceSLOBurn(key: string) {
  return useQuery({
    queryKey: ['service-slo-burn', key],
    queryFn: () => fetchServiceSLOBurn(key),
    enabled: !!key,
    refetchInterval: 30_000,
  })
}

/**
 * The Metrics-portal Explore dock's custom PromQL range query. Enabled only
 * once the operator runs a non-empty query (or a pinned panel supplies one);
 * `paused` freezes the 30s live refetch so an inspected result doesn't get
 * yanked. The query text + window are the cache key so re-running or changing
 * the range refetches. The result carries its own honest gap/error — a bad
 * query is a successful HTTP call whose envelope says gap=true.
 */
export function useMetricsQueryRange(
  query: string,
  sel: WindowSel,
  opts?: { enabled?: boolean; paused?: boolean },
) {
  const enabled = (opts?.enabled ?? true) && query.trim().length > 0
  return useQuery({
    queryKey: ['metrics-query-range', query, windowKey(sel)],
    queryFn: () => fetchMetricsQueryRange(query, sel),
    enabled,
    refetchInterval: opts?.paused ? false : 30_000,
  })
}

/** The Metrics-portal Explore dock's metric catalog (306+ recorded names).
 * Fetched lazily — only when the catalog browser is opened. Rarely changes
 * within a session, so a long staleTime avoids re-pulling the whole list. */
export function useMetricsCatalog(enabled: boolean) {
  return useQuery({
    queryKey: ['metrics-catalog'],
    queryFn: fetchMetricsCatalog,
    enabled,
    staleTime: 5 * 60_000,
  })
}

/** The Metrics-portal top-customers billable-usage table (brief A3). Refetches
 * on a 60s cadence so the table stays live as the metering pipeline records
 * forward; the 30s in-process cache in admind absorbs near-simultaneous panel
 * refetches. */
export function useCustomersUsage() {
  return useQuery({ queryKey: ['customers-usage'], queryFn: fetchCustomersUsage, refetchInterval: 60_000 })
}

/** The per-customer usage-pattern series (range-picker driven). The window is
 * part of the query key so a range change refetches; enabled only once a
 * customer is selected. */
export function useCustomerUsageRange(project: string, sel: WindowSel) {
  return useQuery({
    queryKey: ['customer-usage-range', project, windowKey(sel)],
    queryFn: () => fetchCustomerUsageRange(project, sel),
    enabled: !!project,
    refetchInterval: 60_000,
  })
}

/** service.drain/service.undrain (ADR-0004 slice 1) — invalidates the drained service's own detail query. */
export function useInvokeServiceAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: (_data, { payload }) => {
      const serviceKeys = (payload as { serviceKeys?: string[] } | undefined)?.serviceKeys ?? []
      for (const key of serviceKeys) {
        queryClient.invalidateQueries({ queryKey: ['service', key] })
      }
    },
  })
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: ['search', q],
    queryFn: () => fetchSearch(q),
    enabled: q.trim().length > 0,
  })
}

export function useKBForService(key: string) {
  return useQuery({ queryKey: ['kb', key], queryFn: () => fetchKBForService(key), enabled: !!key })
}

export function useSops(service?: string) {
  return useQuery({ queryKey: ['sops', service ?? ''], queryFn: () => fetchSops(service) })
}

export function useSop(slug: string) {
  return useQuery({ queryKey: ['sop', slug], queryFn: () => fetchSop(slug), enabled: !!slug })
}

export function useSopCoverage() {
  return useQuery({ queryKey: ['sop-coverage'], queryFn: fetchSopCoverage })
}

// --- SOP graph engine (AWU sop-graph-engine): the versioned flowchart-SOP
// fabric. Runs poll on a ~10s cadence so a run walked concurrently (or by an
// agent driver mid-handoff) stays live without a manual refresh — the same
// restart-safe posture as the notification center. All state is DB rows, so a
// poll after an admind restart returns the exact same run.

export function useSopGraphs(includeArchived = false) {
  return useQuery({ queryKey: ['sop-graphs', includeArchived], queryFn: () => fetchSopGraphs(includeArchived) })
}

export function useSopGraph(key: string) {
  return useQuery({ queryKey: ['sop-graph', key], queryFn: () => fetchSopGraph(key), enabled: !!key })
}

export function useSopGraphVersion(key: string, version: number) {
  return useQuery({
    queryKey: ['sop-graph-version', key, version],
    queryFn: () => fetchSopGraphVersion(key, version),
    enabled: !!key && version > 0,
  })
}

export function useSopGraphAnalytics(key: string) {
  return useQuery({ queryKey: ['sop-analytics', key], queryFn: () => fetchSopGraphAnalytics(key), enabled: !!key })
}

export function useSopRuns(sopKey?: string) {
  return useQuery({
    queryKey: ['sop-runs', sopKey ?? ''],
    queryFn: () => fetchSopRuns(sopKey),
    refetchInterval: 10_000,
  })
}

export function useSopRun(id: string) {
  return useQuery({
    queryKey: ['sop-run', id],
    queryFn: () => fetchSopRun(id),
    enabled: !!id,
    refetchInterval: 10_000,
  })
}

/**
 * Every sop.version.* / sop.graph.* / sop.run.* mutation flows through the one
 * gateway lane (invokeAction, fresh Idempotency-Key per call). On success we
 * invalidate the graph/run/analytics/library views the mutation could have
 * changed — simpler and safer than hand-picking which query a given action
 * touched. A dev-token session's mutation still runs but comes back
 * status:"refused" (no do-grant possible) — the caller renders that honestly.
 */
export function useInvokeSopAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sop-graphs'] })
      queryClient.invalidateQueries({ queryKey: ['sop-graph'] })
      queryClient.invalidateQueries({ queryKey: ['sop-graph-version'] })
      queryClient.invalidateQueries({ queryKey: ['sop-analytics'] })
      queryClient.invalidateQueries({ queryKey: ['sop-runs'] })
      queryClient.invalidateQueries({ queryKey: ['sop-run'] })
    },
  })
}

export function useAlerts(filter?: AlertFilter) {
  return useQuery({ queryKey: ['alerts', filter ?? {}], queryFn: () => fetchAlerts(filter) })
}

export function useAlert(id: string) {
  return useQuery({ queryKey: ['alert', id], queryFn: () => fetchAlert(id), enabled: !!id })
}

export function useAlertQuality() {
  return useQuery({ queryKey: ['alert-quality'], queryFn: fetchAlertQuality })
}

export function useSilences() {
  return useQuery({ queryKey: ['silences'], queryFn: fetchSilences })
}

/** The notification center (bell) — the current principal's own feed + unread
 * page badge. Polls on a 30s cadence so a newly escalated alert shows up
 * without a manual refresh, the same live posture as the fleet wall. */
export function useNotifications() {
  return useQuery({ queryKey: ['notifications'], queryFn: fetchNotifications, refetchInterval: 30_000 })
}

/** notification.ack (dismiss + stop escalation) flows through the one mutation
 * lane; on success the bell feed refetches, plus the alerts/silences views the
 * ack could also have changed (acking the notification acks the alert). */
export function useInvokeNotificationAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['silences'] })
    },
  })
}

/** Every alert.* mutation invalidates the alerts/silences/quality views it could have changed — simpler and safer than hand-picking which query a given action affects. */
export function useInvokeAlertAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['alert'] })
      queryClient.invalidateQueries({ queryKey: ['silences'] })
      queryClient.invalidateQueries({ queryKey: ['alert-quality'] })
    },
  })
}

export function useOrgPeople() {
  return useQuery({ queryKey: ['org-people'], queryFn: fetchPeople })
}

export function useOrgAgents() {
  return useQuery({ queryKey: ['org-agents'], queryFn: fetchAgents })
}

export function useOrgRoles() {
  return useQuery({ queryKey: ['org-roles'], queryFn: fetchRoles })
}

export function useOrgTeams() {
  return useQuery({ queryKey: ['org-teams'], queryFn: fetchTeamsList })
}

/**
 * Every org.* management mutation (person/team/role/agent create, member
 * add/remove, grant issue/revoke) invalidates the whole org read surface —
 * small, infrequently-loaded, same posture as useInvokeSecurityAction. A
 * dev-token session sees the forms but the gateway refuses the mutation
 * (403/refused) — that honest 403 is surfaced by the caller, not swallowed.
 */
export function useInvokeOrgAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-people'] })
      queryClient.invalidateQueries({ queryKey: ['org-agents'] })
      queryClient.invalidateQueries({ queryKey: ['org-roles'] })
      queryClient.invalidateQueries({ queryKey: ['org-teams'] })
    },
  })
}

export function useIncidents(filter?: IncidentFilter) {
  return useQuery({ queryKey: ['incidents', filter ?? {}], queryFn: () => fetchIncidents(filter) })
}

export function useIncident(id: string) {
  return useQuery({ queryKey: ['incident', id], queryFn: () => fetchIncident(id), enabled: !!id })
}

export function useIncidentTimeline(id: string) {
  return useQuery({ queryKey: ['incident-timeline', id], queryFn: () => fetchIncidentTimeline(id), enabled: !!id })
}

/** Every incident.* mutation invalidates the incidents list + the specific incident's detail/timeline — the war-room view always reflects the action it just invoked. */
export function useInvokeIncidentAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] })
      queryClient.invalidateQueries({ queryKey: ['incident'] })
      queryClient.invalidateQueries({ queryKey: ['incident-timeline'] })
    },
  })
}

// Terminals poll on a short interval: TTL/idle expiry happens server-side
// (the broker's reaper), not from any client action, so the list/detail
// views need to notice a session going live→expired on their own.
const TERMINAL_POLL_MS = 5000

export function useTerminals(filter?: TerminalFilter) {
  return useQuery({ queryKey: ['terminals', filter ?? {}], queryFn: () => fetchTerminals(filter), refetchInterval: TERMINAL_POLL_MS })
}

export function useTerminal(id: string) {
  return useQuery({ queryKey: ['terminal', id], queryFn: () => fetchTerminal(id), enabled: !!id, refetchInterval: TERMINAL_POLL_MS })
}

export function useTerminalRecording(id: string) {
  return useQuery({ queryKey: ['terminal-recording', id], queryFn: () => fetchTerminalRecording(id), enabled: !!id })
}

/** terminal.open/terminal.kill both invalidate the terminals list + the specific session's detail. */
export function useInvokeTerminalAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminals'] })
      queryClient.invalidateQueries({ queryKey: ['terminal'] })
    },
  })
}

// Security Center R1 (M12 R1: FR-SEC-03/04/05/06).

export function useRotations() {
  return useQuery({ queryKey: ['security-rotations'], queryFn: fetchRotations })
}

export function useVulns() {
  return useQuery({ queryKey: ['security-vulns'], queryFn: fetchVulns })
}

export function useAccessReviewCampaigns() {
  return useQuery({ queryKey: ['security-access-reviews'], queryFn: fetchAccessReviewCampaigns })
}

export function useAccessReviewCampaign(id: string) {
  return useQuery({
    queryKey: ['security-access-review', id],
    queryFn: () => fetchAccessReviewCampaign(id),
    enabled: !!id,
  })
}

export function useBreakGlass() {
  return useQuery({ queryKey: ['security-break-glass'], queryFn: fetchBreakGlass })
}

export function useTerminalReviews() {
  return useQuery({ queryKey: ['security-terminal-reviews'], queryFn: fetchTerminalReviews })
}

/** security.* mutations (rotate/recertify/revoke/report/mark-patched/register/drill/posture-update) all invalidate the whole Security Center — a small, infrequently-loaded module where broad invalidation is simpler than per-action targeting. */
export function useInvokeSecurityAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security-rotations'] })
      queryClient.invalidateQueries({ queryKey: ['security-vulns'] })
      queryClient.invalidateQueries({ queryKey: ['security-access-reviews'] })
      queryClient.invalidateQueries({ queryKey: ['security-access-review'] })
      queryClient.invalidateQueries({ queryKey: ['security-break-glass'] })
      queryClient.invalidateQueries({ queryKey: ['security-terminal-reviews'] })
    },
  })
}

// Pricing module (P2: docs/design/pricing-and-discounts.md §4).

export function usePricingConfig() {
  return useQuery({ queryKey: ['pricing-config'], queryFn: fetchPricingConfig })
}

export function usePricebook() {
  return useQuery({ queryKey: ['pricebook'], queryFn: fetchPricebook })
}

/** The rack book — the book settlement ACTUALLY rates usage against (distinct from usePricebook's legacy rate card). */
export function useRackBook() {
  return useQuery({ queryKey: ['rackbook'], queryFn: fetchRackBook })
}

export function useAccountCredits() {
  return useQuery({ queryKey: ['account-credits'], queryFn: fetchAccountCredits })
}

/** pricing.* mutations (config.update/pricebook.update/rackbook.update/credit.adjust) all invalidate the whole Pricing module — small, infrequently-loaded, same posture as useInvokeSecurityAction. */
export function useInvokePricingAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-config'] })
      queryClient.invalidateQueries({ queryKey: ['pricebook'] })
      queryClient.invalidateQueries({ queryKey: ['rackbook'] })
      queryClient.invalidateQueries({ queryKey: ['account-credits'] })
    },
  })
}

// Infrastructure Fleet (M13 S1/S2: docs/prd/03 Fleet board / Cell page / Node page).

export function useInfrastructureCells() {
  return useQuery({ queryKey: ['infrastructure-cells'], queryFn: fetchInfrastructureCells })
}

export function useInfrastructureCell(id: string) {
  return useQuery({ queryKey: ['infrastructure-cell', id], queryFn: () => fetchInfrastructureCell(id), enabled: !!id })
}

export function useInfrastructureNode(id: string) {
  return useQuery({ queryKey: ['infrastructure-node', id], queryFn: () => fetchInfrastructureNode(id), enabled: !!id })
}

/**
 * fleet.* mutations (M13 S1 records/state-machine + S3 onboard/deboard
 * wizards) all invalidate the fleet-wide cell list, the specific cell a
 * node belongs to, and the specific node — the wizards refetch the live
 * node.state after every step, which is what actually gates each next step
 * (not local wizard state), so a page reload mid-wizard picks up exactly
 * where the gateway left off.
 */
export function useInvokeFleetAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['infrastructure-cells'] })
      queryClient.invalidateQueries({ queryKey: ['infrastructure-cell'] })
      queryClient.invalidateQueries({ queryKey: ['infrastructure-node'] })
    },
  })
}

// M14 Autopilot Command (docs/prd/03 D8) — the separate cockpit.

export function useAutopilotOverview() {
  return useQuery({ queryKey: ['autopilot-overview'], queryFn: fetchAutopilotOverview })
}

export function useAutopilotInbox() {
  return useQuery({ queryKey: ['autopilot-inbox'], queryFn: fetchAutopilotInbox })
}

export function useAutopilotFeed() {
  return useQuery({ queryKey: ['autopilot-feed'], queryFn: fetchAutopilotFeed })
}

/**
 * autopilot.scope.set_mode and the freeze big-button (system.freeze/unfreeze)
 * both invalidate the whole cockpit — the overview carries the scope map, the
 * grade sheet, AND the freeze state, so a single broad invalidation keeps the
 * dashboard honest after any flip. Shortage mode fires many set_mode invokes
 * in one batch, then settles the queries once.
 */
export function useInvokeAutopilotAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, payload }: { actionId: string; payload: unknown }) =>
      invokeAction(actionId, payload, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autopilot-overview'] })
      queryClient.invalidateQueries({ queryKey: ['autopilot-inbox'] })
      queryClient.invalidateQueries({ queryKey: ['autopilot-feed'] })
    },
  })
}
